const chapter = {
  id: 'animal-kingdom',
  slug: 'animal-kingdom',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Animal Kingdom',
        hindi: 'प्राणि जगत',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '4-5 questions in NEET',
        hindi: 'NEET में 4-5 प्रश्न **संगठन का स्तर** — सेलुलर (पोरिफेरा) | कोशिका-ऊतक (सीलेंटरेटा) | ऊतक-अंग (प्लैटिहेल्मिन्थेस) | अंग-प्रणाली (एनेलिडा से आगे)।',
  },
  syllabusTags: ['Diversity in Living World', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'basis-classification',
      title: {
        english: 'Basis of Classification',
        hindi: 'वर्गीकरण का आधार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Levels of organisation** — Cellular (Porifera) | Cell-Tissue (Coelenterata) | Tissue-Organ (Platyhelminthes) | Organ-System (Annelida onwards).',
        hindi: '**संगठन का स्तर** — सेलुलर (पोरिफेरा) | कोशिका-ऊतक (सीलेंटरेटा) | ऊतक-अंग (प्लैटिहेल्मिन्थेस) | अंग-प्रणाली (एनेलिडा से आगे)।',
            },
            {
              english: '**Symmetry** — Asymmetrical (sponges) | Radial (Coelenterata, Ctenophora, Echinoderm adults) | Bilateral (most others).',
        hindi: '**समरूपता** — असममित (स्पंज) | रेडियल (सीलेन्टेरेटा, केटेनोफोरा, इचिनोडर्म वयस्क) | द्विपक्षीय (अधिकांश अन्य)।',
            },
            {
              english: '**Diploblastic** (ectoderm + endoderm; mesoglea between) — Coelenterata, Ctenophora.',
        hindi: '**डिप्लोब्लास्टिक** (एक्टोडर्म + एंडोडर्म; मेसोग्लिया के बीच) - सीलेंटेरटा, केटेनोफोरा।',
            },
            {
              english: '**Triploblastic** (ectoderm + mesoderm + endoderm) — Platyhelminthes onwards.',
        hindi: '**ट्रिप्लोब्लास्टिक** (एक्टोडर्म + मेसोडर्म + एंडोडर्म) - प्लैटिहेल्मिन्थेस से आगे।',
            },
            {
              english: '**Coelom** (body cavity lined by mesoderm):',
        hindi: '**कोइलोम** (मेसोडर्म द्वारा पंक्तिबद्ध शरीर गुहा):',
            },
            {
              english: '• **Coelomate** — Annelida, Mollusca, Arthropoda, Echinodermata, Hemichordata, Chordata.',
        hindi: '• **कोएलोमेट** - एनेलिडा, मोलस्का, आर्थ्रोपोडा, इचिनोडर्मेटा, हेमीकोर्डेटा, कॉर्डेटा।',
            },
            {
              english: '• **Pseudocoelomate** — Aschelminthes (mesoderm in scattered pouches between ectoderm + endoderm).',
        hindi: '• **स्यूडोकोएलोमेट** - एशेलमिन्थेस (एक्टोडर्म + एंडोडर्म के बीच बिखरी हुई थैलियों में मेसोडर्म)।',
            },
            {
              english: '• **Acoelomate** — Platyhelminthes.',
        hindi: '• **एकोएलोमेट** - प्लैटिहेल्मिन्थेस।',
            },
            {
              english: '**Segmentation (metamerism)** — Annelida, Arthropoda, Chordata.',
        hindi: '**विभाजन (मेटामेरिज़्म)** - एनेलिडा, आर्थ्रोपोडा, कॉर्डेटा।',
            },
            {
              english: '**Notochord** — present in Chordata; absent in Non-chordata.',
        hindi: '**नोटोकॉर्ड** - कॉर्डेटा में मौजूद; नॉन-कॉर्डेटा में अनुपस्थित।',
            },
          ],
        },
      ],
    },
    {
      id: 'porifera-coelen-cteno',
      title: {
        english: 'Porifera → Ctenophora',
        hindi: 'पोरिफेरा → केटेनोफोरा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Porifera (sponges)** — primitive multicellular; cellular level; **water canal system** (ostia → spongocoel → osculum); **choanocytes (collar cells)** line spongocoel; skeleton = spicules / spongin fibres; mostly hermaphrodite; reproduction asexual (fragmentation) + sexual. Examples: *Sycon, Spongilla* (freshwater), *Euspongia* (bath sponge).',
        hindi: '**पोरिफेरा (स्पंज)** - आदिम बहुकोशिकीय; सेलुलर स्तर; **जल नहर प्रणाली** (ओस्टिया → स्पोंगोकोल → ऑस्कुलम); **कोआनोसाइट्स (कॉलर कोशिकाएं)** लाइन स्पोंगोकोल; skeleton = spicules / spongin fibres; अधिकतर उभयलिंगी; प्रजनन अलैंगिक (विखंडन) + यौन। उदाहरण: *साइकोन, स्पोंजिला* (मीठा पानी), *यूस्पोंजिया* (स्नान स्पंज)।',
            },
            {
              english: '**Coelenterata / Cnidaria** — aquatic, mostly marine; **tissue level**; radial symmetry; **diploblastic**; **cnidoblasts/nematocysts** for anchorage, defence, prey capture; gastrovascular cavity; **polyp** (sessile) + **medusa** (free-swimming) → **metagenesis** (alternation of generations) e.g. *Obelia*. Examples: *Hydra, Adamsia* (sea anemone), *Pennatula, Gorgonia, Meandrina* (brain coral), *Aurelia* (jelly fish).',
        hindi: '**कोएलेंटरेटा / निडारिया** - जलीय, अधिकतर समुद्री; **ऊतक स्तर**; रेडियल समरूपता; **डिप्लोब्लास्टिक**; **cnidoblasts/nematocysts** लंगरगाह, रक्षा, शिकार पकड़ने के लिए; गैस्ट्रोवास्कुलर कैविटी; **पॉलीप** (सेसाइल) + **मेडुसा** (फ्री-स्विमिंग) → **मेटाजेनेसिस** (पीढ़ी का प्रत्यावर्तन) जैसे। *ओबेलिया*. उदाहरण: *हाइड्रा, एडम्सिया* (समुद्री एनीमोन), *पेनाटुला, गोर्गोनिया, मेनड्रिना* (मस्तिष्क मूंगा), *ऑरेलिया* (जेली फिश)।',
            },
            {
              english: '**Ctenophora (sea walnuts/comb jellies)** — exclusively marine; radially symmetrical, diploblastic; tissue level; **8 external ciliated comb plates** for locomotion; **bioluminescence** very common. Examples: *Pleurobrachia, Ctenoplana*.',
        hindi: '**केटेनोफोरा (समुद्र walnuts/comb जेली)** - विशेष रूप से समुद्री; रेडियल सममित, डिप्लोब्लास्टिक; ऊतक स्तर; ** हरकत के लिए 8 बाहरी सिलिअटेड कंघी प्लेटें**; **बायोलुमिनसेंस** बहुत आम है। उदाहरण: *प्लुरोब्राचिया, केटेनोप्लाना*।',
            },
          ],
        },
      ],
    },
    {
      id: 'platy-aschel-annelida',
      title: {
        english: 'Platyhelminthes → Annelida',
        hindi: 'प्लैटिहेल्मिन्थेस → एनेलिडा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Platyhelminthes (flatworms)** — dorso-ventrally flattened; bilateral, triploblastic, **acoelomate**; organ level; mostly endoparasites; hooks + suckers; **flame cells** for excretion + osmoregulation; hermaphrodite. Examples: *Taenia* (tapeworm), *Fasciola* (liver fluke), *Planaria*.',
        hindi: '**प्लैटिहेल्मिन्थेस (फ्लैटवर्म)** - डोरसो-वेंट्रली चपटा; द्विपक्षीय, त्रिप्लोब्लास्टिक, **एकोएलोमेट**; अंग स्तर; अधिकतर एंडोपरैसाइट्स; हुक + सकर; **उत्सर्जन + ऑस्मोरग्यूलेशन के लिए ज्वाला कोशिकाएं**; उभयलिंगी. उदाहरण: *टेनिया* (टेपवार्म), *फासिओला* (लिवर फ्लूक), *प्लेनेरिया*।',
            },
            {
              english: '**Aschelminthes / Nematoda (round worms)** — body cylindrical (round in cross section); **pseudocoelomate**; organ-system; **alimentary canal complete**; sexes separate (dioecious — males smaller); often **sexual dimorphism**. Examples: *Ascaris* (roundworm), *Wuchereria* (filaria), *Ancylostoma* (hookworm), *Enterobius* (pinworm).',
        hindi: '**एस्केलमिन्थेस / नेमाटोडा (गोल कृमि)** - शरीर बेलनाकार (क्रॉस सेक्शन में गोल); **स्यूडोकोएलोमेट**; अंग-प्रणाली; **आहार नाल पूर्ण**; लिंग अलग-अलग होते हैं (द्विलिंगी - नर छोटे होते हैं); अक्सर **यौन द्विरूपता**। उदाहरण: *एस्करिस* (राउंडवॉर्म), *वुचेरेरिया* (फाइलेरिया), *एंकिलोस्टोमा* (हुकवर्म), *एंटरोबियस* (पिनवर्म)।',
            },
            {
              english: '**Annelida** — first to show **true coelom + metameric segmentation**; bilateral, triploblastic; closed circulatory; nephridia for excretion; nervous system = paired ganglia + ventral nerve cord. Examples: *Nereis* (marine, dioecious), *Pheretima* (earthworm — hermaphrodite), *Hirudinaria* (leech).',
        hindi: '**एनेलिडा** - **सच्चा कोइलोम + मेटामेरिक विभाजन** दिखाने वाला पहला; द्विपक्षीय, त्रिप्लोब्लास्टिक; बंद परिसंचरण; उत्सर्जन के लिए नेफ्रिडिया; घबराया हुआ system = paired ganglia + ventral nerve cord। उदाहरण: *नेरीस* (समुद्री, द्विअर्थी), *फेरेटिमा* (केंचुआ - उभयलिंगी), *हिरुडिनेरिया* (जोंक)।',
            },
          ],
        },
      ],
    },
    {
      id: 'arthropoda-mollusca-echino',
      title: {
        english: 'Arthropoda · Mollusca · Echinodermata · Hemichordata',
        hindi: 'आर्थ्रोपोडा · मोलस्का · इचिनोडर्मेटा · हेमीकोर्डेटा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Arthropoda** — **largest phylum** (2/3 of all named species); jointed appendages; chitinous exoskeleton; head, thorax, abdomen; respiration by **gills, book gills, book lungs, tracheae**; open circulation; **Malpighian tubules** for excretion. Examples — economically: *Apis, Bombyx, Laccifer*; vectors: *Anopheles, Culex, Aedes*; pests: locusts; living fossil: *Limulus* (king crab).',
        hindi: '**आर्थ्रोपोडा** - **सबसे बड़ा संघ** (सभी नामित प्रजातियों में से 2/3); संयुक्त उपांग; चिटिनस एक्सोस्केलेटन; सिर, वक्ष, पेट; **गिल्स, बुक गिल्स, बुक लंग्स, ट्रेकिआ** द्वारा श्वसन; खुला परिसंचरण; **मल्पीघियन नलिकाएं** उत्सर्जन के लिए। उदाहरण - आर्थिक रूप से: *एपिस, बॉम्बेक्स, लैसीफ़र*; वैक्टर: *एनोफ़ेलीज़, क्यूलेक्स, एडीज़*; कीट: टिड्डियाँ; जीवित जीवाश्म: *लिमुलस* (राजा केकड़ा)।',
            },
            {
              english: '**Mollusca** — **second largest phylum**; soft body covered by **calcareous shell**; body has head, muscular foot, visceral hump; **mantle** secretes shell; respiration by **ctenidia (gills)**; **radula** (file-like rasping organ) in mouth. Examples: *Pila* (apple snail), *Pinctada* (pearl oyster), *Sepia* (cuttle fish), *Loligo* (squid), *Octopus, Aplysia* (sea hare), *Dentalium* (tusk shell), *Chaetopleura* (chiton).',
        hindi: '**मोलस्का** - **दूसरा सबसे बड़ा संघ**; मुलायम शरीर **कैलकेरियस शैल** से ढका हुआ; शरीर में सिर, मांसल पैर, आंत कूबड़ है; **मेंटल** खोल को गुप्त करता है; **क्टेनिडिया (गिल्स)** द्वारा श्वसन; **रेडुला** (फ़ाइल जैसा कर्कश अंग) मुँह में। उदाहरण: *पिला* (सेब घोंघा), *पिनक्टाडा* (मोती सीप), *सेपिया* (कटल मछली), *लोलिगो* (स्क्विड), *ऑक्टोपस, अप्लीसिया* (समुद्री खरगोश), *डेंटलियम* (टस्क शेल), *चेटोप्लुरा* (चिटोन)।',
            },
            {
              english: '**Echinodermata** — exclusively marine; adults — **radial symmetry**; larvae bilateral; **water vascular system** (unique) → locomotion + capture + transport of food + respiration; tube feet; endoskeleton of calcareous ossicles; sexes separate. Examples: *Asterias* (starfish), *Echinus* (sea urchin), *Antedon* (sea lily), *Cucumaria* (sea cucumber), *Ophiura* (brittle star).',
        hindi: '**इचिनोडर्मेटा** - विशेष रूप से समुद्री; वयस्क - **रेडियल समरूपता**; लार्वा द्विपक्षीय; **जल संवहनी तंत्र** (अद्वितीय) → गति + ग्रहण + भोजन का परिवहन + श्वसन; ट्यूब पैर; कैलकेरियस अस्थि-पंजर का अंतःकंकाल; लिंग अलग. उदाहरण: *एस्टेरियस* (स्टारफिश), *इचिनस* (समुद्री अर्चिन), *एंटेडॉन* (समुद्री लिली), *कुकुमेरिया* (समुद्री ककड़ी), *ओफिउरा* (भंगुर तारा)।',
            },
            {
              english: '**Hemichordata** — small group; cylindrical body with anterior **proboscis, collar, trunk**; was earlier considered as a sub-phylum of Chordata but now placed under non-chordata; circulatory system open; respiration by gills. Examples: *Balanoglossus, Saccoglossus*.',
        hindi: '**हेमीकोर्डेटा** - छोटा समूह; पूर्वकाल **सूंड, कॉलर, धड़** के साथ बेलनाकार शरीर; पहले इसे कॉर्डेटा का एक उप-फ़ाइलम माना जाता था लेकिन अब इसे गैर-कॉर्डेटा के अंतर्गत रखा गया है; परिसंचरण तंत्र खुला; गलफड़ों द्वारा श्वसन. उदाहरण: *बैलानोग्लोसस, सैकोग्लोसस*।',
            },
          ],
        },
      ],
    },
    {
      id: 'chordata',
      title: {
        english: 'Phylum Chordata',
        hindi: 'संघ कोर्डेटा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Three fundamental characters: **(i) Notochord, (ii) Dorsal hollow nerve cord, (iii) Paired pharyngeal gill slits**. Also: post-anal tail, ventral heart, closed circulation.',
        hindi: 'तीन मूलभूत लक्षण: **(i) नोटोकॉर्ड, (ii) पृष्ठीय खोखली तंत्रिका रज्जु, (iii) युग्मित ग्रसनी गिल स्लिट्स**। इसके अलावा: गुदा के बाद की पूंछ, उदर हृदय, बंद परिसंचरण।',
            },
            {
              english: 'Three sub-phyla:',
        hindi: 'तीन उप-फ़ाइला:',
            },
            {
              english: '• **Urochordata (Tunicata)** — notochord present only in larval tail. Examples: *Ascidia, Salpa, Doliolum*.',
        hindi: '• **यूरोकॉर्डेटा (ट्यूनिकाटा)** - नोटोकॉर्ड केवल लार्वा पूंछ में मौजूद होता है। उदाहरण: *एस्किडिया, सल्पा, डोलिओलम*।',
            },
            {
              english: '• **Cephalochordata** — notochord extends from head to tail; persists throughout life. Example: *Branchiostoma* (Amphioxus / lancelet).',
        hindi: '• **सेफलोकॉर्डेटा** — नोटोकॉर्ड सिर से पूंछ तक फैली हुई है; जीवन भर बना रहता है। उदाहरण: *ब्रैंकियोस्टोमा* (एम्फिऑक्सस / लांसलेट)।',
            },
            {
              english: '• **Vertebrata** — notochord replaced by **vertebral column** in adults; have skull (cranium), brain.',
        hindi: '• **वर्टेब्रेटा** - वयस्कों में नोटोकॉर्ड को **वर्टेब्रल कॉलम** द्वारा प्रतिस्थापित किया जाता है; खोपड़ी (कपाल), मस्तिष्क है।',
            },
            {
              english: 'Vertebrata divided into Agnatha (jawless — Cyclostomata) + Gnathostomata (jawed) → Pisces + Tetrapoda (Amphibia, Reptilia, Aves, Mammalia).',
        hindi: 'वर्टेब्रेटा को एग्नथा (जबड़े रहित - साइक्लोस्टोमेटा) + ग्नथोस्टोमेटा (जबड़े वाले) → मीन + टेट्रापोडा (एम्फीबिया, रेप्टिलिया, एवेस, मैमेलिया) में विभाजित किया गया है।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Class', 'Key features', 'Examples'],
          rows: [
            [
              'Cyclostomata',
              'Jawless; circular sucking mouth; ectoparasites on fish; 6-15 pairs gill slits; cartilage skeleton',
              '*Petromyzon, Myxine*',
            ],
            [
              'Chondrichthyes (cartilaginous fish)',
              'Marine; cartilage skeleton; placoid scales; ventral mouth; **NO air bladder** → swim constantly; some viviparous; internal fertilisation',
              '*Scoliodon, Pristis, Carcharodon, Trygon, Torpedo* (electric ray)',
            ],
            [
              'Osteichthyes (bony fish)',
              'Marine + freshwater; **bony skeleton + air/swim bladder** (regulates buoyancy); 4 pairs gills covered by **operculum**; mostly oviparous; external fertilisation',
              '*Labeo, Catla, Hippocampus* (sea horse), *Exocoetus* (flying fish), *Anabas* (climbing perch), *Clarias* (magur)',
            ],
            [
              'Amphibia',
              '"Two lives" — water + land; respiration by gills/lungs/skin; **3-chambered heart**; ectothermic; mostly oviparous, external fertilisation',
              '*Rana* (frog), *Bufo* (toad), *Hyla* (tree frog), *Salamandra*, *Ichthyophis*',
            ],
            [
              'Reptilia',
              'Creeping; dry skin with **epidermal scales**; **3-chambered heart** (4 in crocodiles); ectothermic; internal fertilisation; oviparous (cleidoic egg with shell)',
              '*Chelone* (turtle), *Testudo* (tortoise), *Hemidactylus* (wall lizard), *Crocodilus, Naja* (cobra), *Vipera, Bungarus*',
            ],
            [
              'Aves (birds)',
              'Forelimbs → wings; hind limbs scales; **endothermic**; **4-chambered heart**; bones pneumatic; long bones hollow; oviparous (eggs with calcareous shell); air sacs; flightless: ostrich',
              '*Corvus* (crow), *Columba* (pigeon), *Psittacula* (parrot), *Pavo* (peacock), *Struthio* (ostrich), *Aptenodytes* (penguin), *Neophron* (vulture)',
            ],
            [
              'Mammalia',
              '**Mammary glands** (milk); hair on body; external **pinnae**; 7 cervical vertebrae; **4-chambered heart**; endothermic; viviparous (mostly); diphyodont; heterodont',
              '*Ornithorhynchus* (egg-laying), *Macropus* (kangaroo), *Pteropus* (flying fox), *Camelus, Macaca* (monkey), *Rattus, Canis, Felis, Elephas, Equus, Delphinus* (dolphin), *Balaenoptera* (blue whale), *Panthera tigris*',
            ],
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Porifera: water canal + choanocytes; cellular level}',
      desc: {
        english: '',
        hindi: 'पोरिफेरा: जल नलिका + कोआनोसाइट्स; सेलुलर स्तर',
      },
    },
    {
      latex: '\\text{Coelenterata: cnidoblasts; polyp + medusa (metagenesis)}',
      desc: {
        english: '',
        hindi: 'सीलेंटेरटा: सिनिडोब्लास्ट; पॉलीप + मेडुसा (मेटाजेनेसिस)',
      },
    },
    {
      latex: '\\text{Pseudocoelom = Aschelminthes only; Acoelom = Platy}',
      desc: {
        english: '',
        hindi: 'स्यूडोकेलोम = केवल एशेलमिन्थेस; एकोएलोम = प्लेटी',
      },
    },
    {
      latex: '\\text{Arthropoda = largest phylum; Mollusca = 2nd largest}',
      desc: {
        english: '',
        hindi: 'आर्थ्रोपोडा = सबसे बड़ा संघ; मोलस्का = दूसरा सबसे बड़ा',
      },
    },
    {
      latex: '\\text{Echinoderm: water vascular system; adults radial, larvae bilateral}',
      desc: {
        english: '',
        hindi: 'इचिनोडर्म: जल संवहनी प्रणाली; वयस्क रेडियल, लार्वा द्विपक्षीय',
      },
    },
    {
      latex: '\\text{Chordate features: notochord + dorsal hollow NC + pharyngeal gill slits + post-anal tail}',
      desc: {
        english: '',
        hindi: 'कॉर्डेट विशेषताएं: नॉटोकॉर्ड + पृष्ठीय खोखला एनसी + ग्रसनी गिल स्लिट्स + पोस्ट-गुदा पूंछ',
      },
    },
    {
      latex: '\\text{Heart chambers: Amphibia/Reptilia 3 (croc 4); Aves/Mammalia 4}',
      desc: {
        english: '',
        hindi: 'हृदय कक्ष: उभयचर/रेप्टिलिया 3 (क्रोक 4); एवेस/स्तनधारी 4',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Phylum',
      problem: {
        english: 'Largest phylum in animal kingdom is:',
        hindi: 'जंतु जगत में सबसे बड़ा संघ है:',
      },
      answer: {
        english: 'Arthropoda',
        hindi: 'आर्थ्रोपोडा',
      },
      solution: {
        english: '~ 2/3 of all known species.',
        hindi: 'सभी ज्ञात प्रजातियों में से ~2/3।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Coelom',
      problem: {
        english: 'Pseudocoelomate phylum is:',
        hindi: 'स्यूडोकोएलोमेट फ़ाइलम है:',
      },
      answer: {
        english: 'Aschelminthes (Nematoda)',
        hindi: 'एशेलमिन्थेस (नेमाटोडा)',
      },
      solution: {
        english: 'Mesoderm scattered pouches.',
        hindi: 'मेसोडर्म बिखरे हुए पाउच।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Echinodermata',
      problem: {
        english: 'Water vascular system is the characteristic feature of:',
        hindi: 'जल संवहनी तंत्र निम्नलिखित में से किसकी विशेषता है?',
      },
      answer: {
        english: 'Echinodermata',
        hindi: 'इचिनोडर्मेटा',
      },
      solution: {
        english: 'Used for locomotion, food capture, respiration.',
        hindi: 'हरकत, भोजन पकड़ने, श्वसन के लिए उपयोग किया जाता है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Chordata',
      problem: {
        english: 'Three fundamental characters of Chordates are notochord, paired pharyngeal gill slits and:',
        hindi: 'कॉर्डेट्स के तीन मूलभूत लक्षण नॉटोकॉर्ड, युग्मित ग्रसनी गिल स्लिट और हैं:',
      },
      answer: {
        english: 'Dorsal hollow nerve cord',
        hindi: 'पृष्ठीय खोखली तंत्रिका रज्जु',
      },
      solution: {
        english: '',
        hindi: 'कॉर्डेट्स की पहचान में **पृष्ठीय खोखली तंत्रिका रज्जु** एक अनिवार्य लक्षण है; अतः यही सही उत्तर है।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Mammal',
      problem: {
        english: 'Egg-laying mammal is:',
        hindi: 'अंडे देने वाला स्तनपायी है:',
      },
      answer: {
        english: 'Ornithorhynchus (Platypus)',
        hindi: 'ऑर्निथोरिन्चस (प्लैटिपस)',
      },
      solution: {
        english: 'Prototherian (oviparous mammal).',
        hindi: 'प्रोटोथेरियन (अंडाकार स्तनपायी)।',
      },
    },
  ],
  next: {
    slug: 'morphology-flowering-plants',
    title: 'Morphology of Flowering Plants',
  },
};

export default chapter;
